import createExportUrl from './helper/createExportUrl';
import { getDiscoveryKPICities } from 'url-constructors/Shazam/getDiscoveryKPICities';
import { getDiscoveryKPICountries } from 'url-constructors/Shazam/getDiscoveryKPICountries';
import kyWithSentry from './helper/kyWithSentry';
import getShazamTableData from 'url-constructors/Shazam/getShazamTableData';
import { isArray } from 'lodash';

const getShazamTagsData = (options: any): any => {
  return options.tagType === 'countries'
    ? kyWithSentry.get(getDiscoveryKPICountries({ ...options })).json()
    : kyWithSentry.get(getDiscoveryKPICities({ ...options })).json();
};

const getShazamTrendData = (kpiData: any, options: any): any => {
  if (options.tagType === 'countries') {
    const ids = kpiData[0].countries.map((c: any) => c.countryId);
    return kyWithSentry
      .get(
        getShazamTableData({
          ...options,
          countryIds: ids,
        })
      )
      .json();
  } else {
    const ids = kpiData[0].cities.map((c: any) => c.cityId);
    return kyWithSentry
      .get(
        getShazamTableData({
          ...options,
          cityIds: ids,
        })
      )
      .json();
  }
};

interface IShazamInterceptionData {
  entityIds: string[];
  entityType: 'artist' | 'track';
  tagType: 'cities' | 'countries';
  sortBy: string;
  sortOrder: string;
  limit: number;
  offset: number;
  countryIds: string | string[];
  searchQuery?: string;
  withTrends?: boolean;
  region?: string | string[];
  customRegion?: string | string[];
}

interface IShazamKPIData {
  countLastLastWeek: number;
  countLastWeek: number;
  countLastWeekToDate: number;
  countThisWeekToDate: number;
  wowInProgress: number;
  wowLastCompleted: number;
}

export interface IShazamCity {
  cityId: number;
  cityName: string;
  data: IShazamKPIData;
  countryId: string;
  stateCode: string;
  stateName: string;
}

interface IShazamCountry {
  countryName: string;
  data: IShazamKPIData;
  countryId: string;
}

interface IShazamCitiesData {
  cities: IShazamCity[];
  entity: any;
  totalCitiesCount: number;
}

interface IShazamCountriesData {
  countries: IShazamCountry[];
  entity: any;
  totalCountryCount: number;
}

interface IShazamTrendCityItem {
  cities: any[];
  periodKey: string;
  totalCitiesCount: number;
}

interface IShazamTrendCountryItem {
  countries: any[];
  periodKey: string;
  totalCountriesCount: number;
}

interface IShazamTrendData {
  entity: any;
  tableData: IShazamTrendCityItem[] | IShazamTrendCountryItem[];
}

const getCountryIds = (
  region?: string | string[],
  countryIds?: string | string[],
  customRegion?: string | string[]
): string[] => {
  const result: string[] = [];

  const getTerritoryList = (terrType?: string | string[]): string[] => {
    const result: string[] = [];

    if (terrType) {
      if (isArray(terrType)) {
        result.push(...terrType);
      } else {
        result.push(terrType);
      }
    }

    return result;
  };
  result.push(...getTerritoryList(region));
  result.push(...getTerritoryList(countryIds));
  result.push(...getTerritoryList(customRegion));
  return result;
};

const shazamTagInterceptor = async (
  input: RequestInfo /* eslint-disable-line */,
  init: RequestInit | undefined /* eslint-disable-line */,
  interceptionData: IShazamInterceptionData
): Promise<Response> => {
  const {
    entityIds,
    entityType,
    tagType,
    sortBy,
    sortOrder,
    limit,
    offset,
    countryIds,
    searchQuery,
    withTrends,
    region,
    customRegion,
  } = interceptionData;
  const options: any = {
    entityIds,
    entityType,
    limit,
    offset,
    sortOrder,
    sortBy,
  };

  const trendOptions = {
    ...options,
    limit: 30,
    offset: 0,
    tagType,
    sortBy: undefined,
  };

  const isCountries: boolean = tagType === 'countries';

  const searchOptions = isCountries
    ? { countryName: searchQuery }
    : { cityNameFilter: searchQuery };

  const apiUrl: string = isCountries
    ? getDiscoveryKPICountries({
        ...options,
        countryIds: countryIds || undefined,
        region,
        customRegion,
        ...searchOptions,
        regionBreakdown: 'country',
      })
    : getDiscoveryKPICities({
        ...options,
        ...searchOptions,
        countryIds: countryIds || undefined,
        region,
        customRegion,
        regionBreakdown: 'country',
      });

  try {
    const shazamKPIData:
      | IShazamCitiesData
      | IShazamCountriesData = await getShazamTagsData({
      ...options,
      tagType,
      ...searchOptions,
      countryIds: countryIds,
      region,
      customRegion,
      regionBreakdown: 'country',
    });
    const shazamTrendData: IShazamTrendData = withTrends
      ? await getShazamTrendData(shazamKPIData, trendOptions)
      : null;
    const exportUrl = createExportUrl(apiUrl);
    const shazamKPIFilterData =
      isCountries && (countryIds || region || customRegion)
        ? await getShazamTagsData({
            ...options,
            tagType,
            countryIds: countryIds,
            regionId: region,
            customRegion,
          })
        : null;

    const shazamTrendFilterData =
      withTrends && isCountries && (countryIds || region || customRegion)
        ? await getShazamTrendData(shazamKPIFilterData, {
            ...trendOptions,
            countryIds: [...getCountryIds(region, countryIds, customRegion)],
          })
        : null;

    return new Response(
      JSON.stringify({
        shazamKPIData,
        shazamTrendData,
        shazamKPIFilterData,
        shazamTrendFilterData,
        exportUrl,
      })
    );
  } catch (error) {
    const errorInfo = await error.response.json();
    return new Response(
      JSON.stringify({
        reason: {
          error: errorInfo.message,
          errorInfo,
        },
      })
    );
  }
};

export default shazamTagInterceptor;
